import { ChangeEvent, useMemo, useReducer, useState } from "react";
import { FileEditAltIcon, IconSet, TextInput, ToggleInput } from "@datagame/ui";
import { useTranslation } from "react-i18next";

import { ActionButtons } from "../../Common/ActionButtons";
import { BoardBackground } from "./BoardBackground";
import { ColorInput } from "../../../../../../shared/ui/ColorInput";
import { Container, Heading, ImageInput, LeftSide, RightSide, Section } from "../../Common";
import {
  Game,
  GameVisualsUpdateInput,
  LibraryImage,
  LibraryImageKind,
  IconSet as IconSetEnum,
  SwiperConfigurationUpdateInput,
  GameKind,
} from "../../../../../../../gql/graphql";
import { GamePreview } from "../../../GamePreview";
import { isCardBasedGame } from "../../../../../../../graphql/helpers/CardBasedGame";
import { isRankifierGame } from "../../../../../../../graphql/helpers/isRankifierGame";
import { isSwiperGame } from "../../../../../../../graphql/helpers/SwiperGame";
import { isWordopolisGame } from "../../../../../../../graphql/helpers/isWordopolisGame";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";
import { OppositeColors } from "./OppositeColors";
import { isTriviaGame } from "../../../../../../../graphql/helpers/isTriviaGame";

import styles from "./Form.module.scss";

export enum ActionTypes {
  updateThemeColor = "updateThemeColor",
  updateCardBackImage = "updateCardBackImage",
  updateShowIcons = "updateShowIcons",
  updateBackgroundImage = "updateBackgroundImage",
  updateBackgroundColor = "updateBackgroundColor",
  updateBackgroundPositiveColor = "updateBackgroundPositiveColor",
  updateBackgroundNegativeColor = "updateBackgroundNegativeColor",
  updateIconSet = "updateIconSet",
  updatePresentLetter = "updatePresentLetter",
  updateCorrectLetter = "updateCorrectLetter",
  updateDefaultLetter = "updateDefaultLetter",
  updateAbsentLetter = "updateAbsentLetter",
}

export type Actions =
  | { type: ActionTypes.updateThemeColor; themeColor: string }
  | { type: ActionTypes.updateCardBackImage; image: LibraryImage | null }
  | { type: ActionTypes.updateShowIcons; showIcons: boolean }
  | { type: ActionTypes.updateBackgroundImage; image: LibraryImage | null }
  | { type: ActionTypes.updateBackgroundColor; color: string }
  | { type: ActionTypes.updateBackgroundPositiveColor; color: string }
  | { type: ActionTypes.updateBackgroundNegativeColor; color: string }
  | { type: ActionTypes.updateIconSet; iconSet: IconSetEnum }
  | { type: ActionTypes.updatePresentLetter; color: string }
  | { type: ActionTypes.updateCorrectLetter; color: string }
  | { type: ActionTypes.updateAbsentLetter; color: string }
  | { type: ActionTypes.updateDefaultLetter; color: string };

export type State = {
  themeColor: string;
  cardBackImage: LibraryImage | null;
  showIcons: boolean;
  backgroundImage: LibraryImage | null;
  backgroundColor: string;
  backgroundPositiveColor: string;
  backgroundNegativeColor: string;
  iconSet: IconSetEnum;
  presentLetter: string;
  correctLetter: string;
  defaultLetter: string;
  absentLetter: string;
};

export const reducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case ActionTypes.updateThemeColor:
      return { ...state, themeColor: action.themeColor };
    case ActionTypes.updateCardBackImage:
      return { ...state, cardBackImage: action.image };
    case ActionTypes.updateShowIcons:
      return { ...state, showIcons: action.showIcons };
    case ActionTypes.updateBackgroundImage:
      return { ...state, backgroundImage: action.image };
    case ActionTypes.updateBackgroundColor:
      return { ...state, backgroundColor: action.color };
    case ActionTypes.updateBackgroundPositiveColor:
      return { ...state, backgroundPositiveColor: action.color };
    case ActionTypes.updateBackgroundNegativeColor:
      return { ...state, backgroundNegativeColor: action.color };
    case ActionTypes.updateIconSet:
      return { ...state, iconSet: action.iconSet };
    case ActionTypes.updatePresentLetter:
      return { ...state, presentLetter: action.color };
    case ActionTypes.updateCorrectLetter:
      return { ...state, correctLetter: action.color };
    case ActionTypes.updateDefaultLetter:
      return { ...state, defaultLetter: action.color };
    case ActionTypes.updateAbsentLetter:
      return { ...state, absentLetter: action.color };
  }
};

export interface CustomizeVisualsFormProps {
  game: Game;
  onUpdate: (gameVisuals: GameVisualsUpdateInput) => void;
  onUpdateSwiperConfiguration: (swiperConfiguration: SwiperConfigurationUpdateInput) => void;
}

export const Form = ({ game, onUpdateSwiperConfiguration, onUpdate }: CustomizeVisualsFormProps) => {
  const { t } = useTranslation();

  const [state, dispatch] = useReducer(reducer, game.configuration.gameVisuals as State);
  const [showButtons, setShowButtons] = useState(isSwiperGame(game) ? game.configuration.showButtons : false);
  const [likeText, setLikeText] = useState(isSwiperGame(game) ? game.configuration.likeText : "");
  const [unlikeText, setUnlikeText] = useState(isSwiperGame(game) ? game.configuration.unlikeText : "");

  const [isUsingImage, setIsUsingImage] = useState(Boolean(state.backgroundImage));

  const handleUploadBackImage = (image: LibraryImage) => {
    dispatch({ type: ActionTypes.updateCardBackImage, image });
    onUpdate({ cardBackImageId: image.id });
  };

  const handleChangeShowIcons = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: ActionTypes.updateShowIcons, showIcons: event.target.checked });
    onUpdate({ showIcons: event.target.checked });
  };

  const handleUpdate = () => {
    onUpdate({
      themeColor: state.themeColor,
      cardBackImageId: state.cardBackImage?.id,
      showIcons: state.showIcons,
      backgroundImageId: state.backgroundImage?.id,
      backgroundColor: state.backgroundColor,
      backgroundPositiveColor: state.backgroundPositiveColor,
      backgroundNegativeColor: state.backgroundNegativeColor,
      iconSet: state.iconSet,
      presentLetter: state.presentLetter,
      correctLetter: state.correctLetter,
      defaultLetter: state.defaultLetter,
    });
  };

  const handleUpdateSwiperConfiguration = () => {
    onUpdateSwiperConfiguration({
      showButtons,
      likeText,
      unlikeText,
    });
  };

  const handleRemoveBackgroundImage = () => {
    dispatch({ type: ActionTypes.updateBackgroundImage, image: null });
    onUpdate({ backgroundImageId: null });
  };

  const handleUploadBackgroundImage = (image: LibraryImage) => {
    dispatch({ type: ActionTypes.updateBackgroundImage, image });

    onUpdate({ backgroundImageId: image.id });
  };

  const handleIsUsingImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    if (!value) {
      handleRemoveBackgroundImage();
    }
    setIsUsingImage(event.target.checked);
  };

  const handleChangeShowButtons = (event: ChangeEvent<HTMLInputElement>) => {
    setShowButtons(event.target.checked);
    onUpdateSwiperConfiguration({
      showButtons: event.target.checked,
    });
  };

  const isCardBased = useMemo(() => isCardBasedGame(game), [game]);
  const isSwiper = useMemo(() => isSwiperGame(game), [game]);
  const isWordle = useMemo(() => game.kind === GameKind.Wordle, [game]);

  const description = isWordle
    ? t("project:customizeVisuals.wordleDescription")
    : t("project:customizeVisuals.description");

  const renderBackgroundSectionContent = () => {
    if (isUsingImage) {
      return (
        <ImageInput
          image={state.backgroundImage}
          kind={LibraryImageKind.Background}
          onUpload={handleUploadBackgroundImage}
          onRemove={handleRemoveBackgroundImage}
          showRemoveButton={true}
        />
      );
    }

    if (isSwiperGame(game) || isRankifierGame(game)) {
      return (
        <div className={styles.backgroundColors}>
          <Section
            title={t("project:customizeVisuals.form.positiveColor")}
            className={styles.sectionWithoutMargin}
            tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.backgroundPositiveColorTooltip")} />}
          >
            <ColorInput
              value={state.backgroundPositiveColor}
              onChange={(color: string) => dispatch({ type: ActionTypes.updateBackgroundPositiveColor, color })}
              onBlur={handleUpdate}
            />
          </Section>
          <Section
            title={t("project:customizeVisuals.form.negativeColor")}
            className={styles.sectionWithoutMargin}
            tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.backgroundNegativeColorTooltip")} />}
          >
            <ColorInput
              value={state.backgroundNegativeColor}
              onChange={(color: string) => dispatch({ type: ActionTypes.updateBackgroundNegativeColor, color })}
              onBlur={handleUpdate}
            />
          </Section>
        </div>
      );
    }

    return (
      <ColorInput
        value={state.backgroundColor}
        onChange={(color: string) => dispatch({ type: ActionTypes.updateBackgroundColor, color })}
        onBlur={handleUpdate}
      />
    );
  };

  return (
    <Container>
      <RightSide>
        <GamePreview isFlickable={true} />
      </RightSide>
      <LeftSide>
        <Heading title={{ main: t("project:customizeVisuals.header1"), marked: t("project:customizeVisuals.header2") }}>
          {description}
        </Heading>

        {!isWordle && (
          <div className={styles.fieldsWrapper}>
            {!isTriviaGame(game) && (
              <Section
                title={t("project:customizeVisuals.form.theme")}
                className={styles.sectionWithoutMargin}
                tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.themeTooltip")} />}
              >
                <ColorInput
                  value={state.themeColor}
                  onChange={(color: string) => dispatch({ type: ActionTypes.updateThemeColor, themeColor: color })}
                  onBlur={handleUpdate}
                />
              </Section>
            )}

            {isCardBased && (
              <Section
                title={t("project:customizeVisuals.form.cardBackImage")}
                className={styles.sectionWithoutMargin}
                tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.cardBackImageTooltip")} />}
              >
                <ImageInput
                  image={state.cardBackImage}
                  onUpload={handleUploadBackImage}
                  kind={LibraryImageKind.CardBack}
                />
              </Section>
            )}

            {(isSwiperGame(game) || isRankifierGame(game)) && (
              <OppositeColors gameVisuals={game.configuration.gameVisuals} />
            )}

            <Section
              title={t(`project:customizeVisuals.form.${isUsingImage ? "backgroundImage" : "backgroundColor"}`)}
              className={styles.sectionWithoutMargin}
              tooltip={
                <TooltipWithIcon
                  content={t(
                    `project:customizeVisuals.form.${
                      isUsingImage ? "backgroundImageTooltip" : "backgroundColorTooltip"
                    }`,
                  )}
                />
              }
            >
              {renderBackgroundSectionContent()}
              <ToggleInput
                className={styles.toggle}
                onChange={handleIsUsingImageChange}
                value={isUsingImage}
                label={t("project:gameResults.useImage") ?? ""}
                name="transfer"
                tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.useImageTooltip")} />}
              />
            </Section>

            {isSwiper && (
              <div className={styles.backgroundColors}>
                <Section
                  title={t("project:customizeVisuals.form.likeText")}
                  className={styles.sectionWithoutMargin}
                  tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.likeTextTooltip")} />}
                >
                  <TextInput
                    icon={<FileEditAltIcon />}
                    size="small"
                    value={likeText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setLikeText(event.target.value)}
                    onBlur={handleUpdateSwiperConfiguration}
                  />
                </Section>

                <Section
                  title={t("project:customizeVisuals.form.unlikeText")}
                  className={styles.sectionWithoutMargin}
                  tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.unlikeTextTooltip")} />}
                >
                  <TextInput
                    icon={<FileEditAltIcon />}
                    size="small"
                    value={unlikeText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setUnlikeText(event.target.value)}
                    onBlur={handleUpdateSwiperConfiguration}
                  />
                </Section>
              </div>
            )}

            {isCardBased && (
              <Section
                title={t("project:customizeVisuals.form.iconSets")}
                className={styles.sectionWithoutMargin}
                tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.iconSetsTooltip")} />}
              >
                <div className={styles.iconSets}>
                  {Object.values(IconSetEnum).map((iconSet) => (
                    <IconSet
                      key={iconSet}
                      iconSetKey={iconSet}
                      active={iconSet === state.iconSet}
                      onClick={() => {
                        dispatch({ type: ActionTypes.updateIconSet, iconSet });
                        onUpdate({ iconSet: iconSet });
                      }}
                    />
                  ))}
                </div>
              </Section>
            )}

            {isCardBased && (
              <Section title={t("project:customizeVisuals.form.preferences")} className={styles.sectionWithoutMargin}>
                <ToggleInput
                  className={styles.toggle}
                  value={state.showIcons}
                  name="show-icons"
                  label={t("project:customizeVisuals.form.showIcons") ?? ""}
                  onChange={handleChangeShowIcons}
                  tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.showIconsTooltip")} />}
                />

                {isSwiperGame(game) && (
                  <ToggleInput
                    className={styles.toggle}
                    value={showButtons}
                    name="show-buttons"
                    label={t("project:customizeVisuals.form.showButtons") ?? ""}
                    onChange={handleChangeShowButtons}
                    tooltip={<TooltipWithIcon content={t("project:customizeVisuals.form.showButtonsTooltip")} />}
                  />
                )}
              </Section>
            )}

            {isWordopolisGame(game) && <BoardBackground game={game} />}

            <ActionButtons />
          </div>
        )}
      </LeftSide>
    </Container>
  );
};
